import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { Globe, BarChart, TrendingUp, Users } from 'lucide-react';
import { ASSETS } from '../config/assets';

const GlobalReviewPage = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: BarChart,
      title: t('globalReview.feature1Title'),
      desc: t('globalReview.feature1Desc'),
    },
    {
      icon: TrendingUp,
      title: t('globalReview.feature2Title'),
      desc: t('globalReview.feature2Desc'),
    },
    {
      icon: Users,
      title: t('globalReview.feature3Title'),
      desc: t('globalReview.feature3Desc'),
    },
  ];

  return (
    <div data-testid="global-review-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-b from-[#1A1A1A] to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-8 h-8 text-wine" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine">
                  {t('globalReview.title')}
                </span>
              </div>
              <h1 
                data-testid="global-review-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
              >
                {t('globalReview.subtitle')}
              </h1>
              <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
                {t('globalReview.heroDesc')}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="global-review-cta"
                  className="btn-demo-cta text-white rounded-lg px-8 py-6"
                >
                  {t('nav.requestDemo')}
                </Button>
              </Link>
            </div>
            <div className="hidden lg:flex justify-start pl-8">>
              <img 
                src="https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/09i3tw0t_7C14F1D6-98E2-4B0D-BEA5-4CCF331022AD.png"
                alt="DelFriscos Review Platform"
                className="w-[420px] h-auto rounded-3xl shadow-2xl shadow-black/50"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={`global-feature-${idx}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-wine/30 transition-all duration-300"
              >
                <div className="p-3 bg-wine/10 rounded-xl w-fit mb-6">
                  <feature.icon className="w-6 h-6 text-wine" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Visual Section */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              {t('globalReview.dashboardTitle')}
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              {t('globalReview.dashboardDesc')}
            </p>
          </div>
          <div data-testid="global-review-dashboard-image" className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
            <img
              src={ASSETS.GLOBAL_REVIEW_DASHBOARD}
              alt="Multi-location review dashboard"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlobalReviewPage;
